import type {
  SelectableItem,
  SleepExperienceOptionId,
  SleepingPatternId,
  YesNo,
} from '@physitrack/championhealth-types';

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Checkbox } from '../questions/checkbox/Checkbox';
import { CheckboxRadio } from '../questions/checkboxRadio/CheckboxRadio';
import { Clock } from '../questions/clock/Clock';
import { OneToTenSlider } from '../questions/painSlider/OneToTenSlider';
import { Radio } from '../questions/radio/Radio';
import { AssessmentPageItem } from '../types/AssessmentPageItem';

export function Sleep1({
  active,
  assessmentValues,
  questionid,
  questionHeading,
  questionText,
  onOpenModal,
  onSetValue,
}: AssessmentPageItem) {
  const { t } = useTranslation();
  const sleepQualityTextValue = [t('SL001'), t('SL002')];

  const sleepExperienceOptions: SelectableItem<SleepExperienceOptionId>[] = [
    { id: 'none', title: t('SL003') },
    { id: 'sleep_apnoea', title: t('SL004') },
    { id: 'insomnia', title: t('SL005') },
    { id: 'restless_leg_syndrome', title: t('SL006') },
    { id: 'difficulty_getting_to_sleep', title: t('SL007') },
    {
      id: 'waking_up_regularly_at_night',
      title: t('SL008'),
    },
  ];

  const nightShiftOptions: SelectableItem<YesNo>[] = [
    { id: 'yes', title: t('SL009') },
    { id: 'no', title: t('SL010') },
  ];

  const sleepPatternOptions: SelectableItem<SleepingPatternId>[] = [
    { id: 'regular', title: t('SL011') },
    { id: 'somewhat_irregular', title: t('SL012') },
    { id: 'irregular', title: t('SL013') },
  ];

  useEffect(() => {
    if (assessmentValues.night_shift === 'yes') {
      onSetValue({
        id: 'sleeping_pattern',
        value: null,
      });
    }
  }, [assessmentValues.night_shift, onSetValue]);

  return (
    <div className={`question_container ${active}`} id={questionid}>
      <h1>{questionHeading}</h1>
      <p>{questionText}</p>
      <Clock
        questionid="clock_bed"
        width=""
        questionTitle={t('assessment.question.clock_bed')}
        startHour={22}
        startMinute={0}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
      />
      <Clock
        questionid="clock_wake"
        width=""
        questionTitle={t('assessment.question.clock_wake')}
        startHour={7}
        startMinute={0}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
      />
      <OneToTenSlider
        questionid="quality_of_sleep"
        questionTitle={t('assessment.question.quality_of_sleep')}
        width="_wide"
        textValues={sleepQualityTextValue}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('SL017')}
        onOpenModal={onOpenModal}
      />
      <Checkbox
        questionid="sleep_experience"
        width="_wide"
        questionTitle={t('assessment.question.sleep_experience')}
        questionTitleSuffix={t('SL019')}
        options={sleepExperienceOptions}
        itemLimit="3"
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('SL021')}
        onOpenModal={onOpenModal}
      />

      <Radio
        questionid="night_shift"
        width=""
        questionTitle={t('assessment.question.night_shift')}
        options={nightShiftOptions}
        onSetValue={onSetValue}
        assessmentValues={assessmentValues}
        modalText={t('SL023')}
        onOpenModal={onOpenModal}
      />
      {assessmentValues.night_shift === 'no' && (
        <CheckboxRadio
          questionid="sleeping_pattern"
          width=""
          options={sleepPatternOptions}
          questionTitle={t('assessment.question.sleeping_pattern')}
          onSetValue={onSetValue}
          assessmentValues={assessmentValues}
          fadeIn="yes"
          modalText={t('SL026')}
          onOpenModal={onOpenModal}
        />
      )}
    </div>
  );
}
